import { z } from 'zod';
import { clarificationQuestionSchema } from './clarification-episode.schemas.js';
function utf8ByteLength(s) {
    return new TextEncoder().encode(s).length;
}
/** FR-016 metric label constants (bounded cardinality). */
export const CLARIFICATION_RESUME_METRIC_OUTCOME = {
    accepted: 'accepted',
    duplicate_noop: 'duplicate_noop',
    denied_expired: 'denied_expired',
    denied_superseded: 'denied_superseded',
    denied_unauthorized: 'denied_unauthorized',
    denied_not_open: 'denied_not_open',
    enqueue_transient_failure: 'enqueue_transient_failure',
    enqueue_exhausted: 'enqueue_exhausted',
};
export const clarificationAnswerSourceChannelSchema = z.enum(['ui', 'inbox', 'jira', 'linear']);
export const clarificationAnswerEntrySchema = z
    .object({
    question_id: z.string().uuid(),
    text: z.string().min(1).max(32_000),
})
    .strict();
export const clarificationResponseSubmitRequestSchema = z
    .object({
    answers: z.array(clarificationAnswerEntrySchema).min(1).max(32),
    client_idempotency_key: z.string().max(256).optional(),
})
    .strict();
const questionsArraySchema = z.array(clarificationQuestionSchema).min(1).max(3);
export function validateClarificationAnswersAgainstQuestions(params) {
    const qParsed = questionsArraySchema.safeParse(params.questionsJson);
    if (!qParsed.success) {
        return { ok: false, message: 'Invalid episode questions payload' };
    }
    const questions = qParsed.data;
    const requiredIds = new Set(questions.map((q) => q.id));
    if (requiredIds.size !== questions.length) {
        return { ok: false, message: 'Duplicate question ids in episode' };
    }
    const seen = new Set();
    let totalBytes = 0;
    for (const a of params.answers) {
        if (seen.has(a.question_id)) {
            return { ok: false, message: 'Duplicate answer question_id' };
        }
        seen.add(a.question_id);
        totalBytes += utf8ByteLength(a.text);
        if (totalBytes > 96_000) {
            return { ok: false, message: 'Answers exceed aggregate size bound' };
        }
    }
    for (const id of requiredIds) {
        if (!seen.has(id)) {
            return { ok: false, message: `Missing answer for question ${id}` };
        }
    }
    for (const id of seen) {
        if (!requiredIds.has(id)) {
            return { ok: false, message: `Unexpected answer question_id ${id}` };
        }
    }
    return {
        ok: true,
        normalizedAnswersJson: params.answers.map((a) => ({
            question_id: a.question_id,
            text: a.text,
        })),
    };
}
export const clarificationResponseSubmitOutcomeSchema = z.enum([
    'accepted',
    'duplicate_noop',
    'denied_expired',
    'denied_superseded',
    'denied_unauthorized',
    'enqueue_pending_retry',
    'enqueue_exhausted',
]);
export const clarificationResumeStateSchema = z.enum([
    'awaiting_answers',
    'response_received',
    'resume_pending',
    'generation_resumed',
    'expired',
    'superseded',
    'duplicate_noop',
    'enqueue_failed',
]);
export const inboxClarificationCompleteBodySchema = z
    .object({
    episode_id: z.string().uuid(),
    answers: z.array(clarificationAnswerEntrySchema).min(1).max(32),
    client_idempotency_key: z.string().max(256).optional(),
})
    .strict();
export function deriveClarificationResumeFields(input) {
    const { episodeDisposition: d, attemptLifecycleState: life } = input;
    if (d === 'open' && life === 'paused') {
        return {
            resume_state: 'awaiting_answers',
            resume_reason: 'Awaiting complete clarification answers.',
        };
    }
    if (d === 'answered') {
        if (life === 'queued' || life === 'running') {
            return {
                resume_state: 'generation_resumed',
                resume_reason: 'Specification generation has resumed.',
            };
        }
        if (life === 'paused') {
            return {
                resume_state: 'enqueue_failed',
                resume_reason: 'Answers were accepted but the generation job is still paused (enqueue may have failed).',
            };
        }
        if (life === 'completed') {
            return {
                resume_state: 'generation_resumed',
                resume_reason: 'Specification generation completed.',
            };
        }
        return {
            resume_state: 'resume_pending',
            resume_reason: 'Clarification answered; waiting on dispatch state updates.',
        };
    }
    if (d === 'timed_out') {
        return { resume_state: 'expired', resume_reason: 'Clarification episode timed out.' };
    }
    if (d === 'superseded') {
        return { resume_state: 'superseded', resume_reason: 'Clarification episode was superseded.' };
    }
    return {
        resume_state: 'resume_pending',
        resume_reason: 'Clarification status is updating.',
    };
}
//# sourceMappingURL=clarification-resume.schemas.js.map